import { Box, Grid, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ArrowLink, Eyebrow, PrimaryButton, Section, SectionHeader, SecondaryButton } from "../ui/primitives";
import { colors, company } from "../ui/tokens";
import { products, projectById } from "../data/projects";
import ProjectCard from "../widgets/ProjectCard";
import ProductFeature from "../widgets/ProductFeature";
import CtaBand from "../widgets/CtaBand";

const CLIENTS = ["MADRE", "Swisscontact", "CEC-FPI", "Community Hearings", "Step In, Step Out", "DevOpsQuest"];
// Client projects only: our own products have their own section.
const FEATURED = { large: 8, grid: [2, 5, 4] };

function Hero() {
  const { t, i18n } = useTranslation("HomePage");
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  return (
    <Section tone="warm" py={{ xs: 7, md: 10 }}>
      <Grid container spacing={{ xs: 6, md: 6 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
          <Typography variant="h1" component="h1" sx={{ color: colors.ink, fontSize: { md: "3.1rem", lg: "3.6rem" } }}>
            {t("hero.title")}
          </Typography>
          <Typography sx={{ mt: 3, fontSize: { xs: "1.05rem", md: "1.2rem" }, lineHeight: 1.65, color: colors.body, maxWidth: 560 }}>
            {t("hero.lead")}
          </Typography>
          <Box sx={{ mt: 4.5, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <PrimaryButton to="/products">{t("hero.primary")}</PrimaryButton>
            <SecondaryButton to="/contact">{t("hero.secondary")}</SecondaryButton>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {/* OXIPOS on a Mac with Posa Sport on a phone in front of it. */}
          <Box component="figure" sx={{ m: 0 }}>
            <Box sx={{ position: "relative", pr: { xs: "14%", md: "12%" }, pb: { xs: "10%", md: "8%" } }}>
              <Box
                component="img"
                src={`/assets/projects/oxipos/hero-${lang}.jpg`}
                alt="OXIPOS"
                sx={{ width: "100%", height: "auto", borderRadius: "16px", boxShadow: "0 30px 60px -20px rgba(14,23,48,0.45)" }}
              />
              <Box
                component="img"
                src="/assets/projects/posa/posa-phone-explore.png"
                alt="Posa Sport"
                sx={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: { xs: "30%", md: "28%" },
                  height: "auto",
                  filter: "drop-shadow(0 24px 30px rgba(14,23,48,0.35))",
                }}
              />
            </Box>
            <Typography component="figcaption" sx={{ mt: 1.75, fontSize: "0.85rem", color: colors.muted }}>
              {t("hero.caption")}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
}

function Clients() {
  const { t } = useTranslation("HomePage");
  return (
    <Section tone="paper" py={{ xs: 4, md: 4.5 }} sx={{ borderTop: `1px solid ${colors.line}`, borderBottom: `1px solid ${colors.line}` }}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: 5 }, alignItems: { md: "center" } }}>
        <Typography sx={{ fontSize: "0.85rem", color: colors.muted, flexShrink: 0, maxWidth: { md: 170 }, lineHeight: 1.4 }}>
          {t("clients.label")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: { xs: 3, md: 4.5 }, rowGap: 1.5, alignItems: "center" }}>
          {CLIENTS.map((name) => (
            <Typography key={name} sx={{ fontSize: { xs: "1rem", md: "1.12rem" }, fontWeight: 700, color: colors.navyMid, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
              {name}
            </Typography>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function Products() {
  const { t } = useTranslation("HomePage");
  return (
    <Box id="products">
      <Section tone="paper" py={{ xs: 8, md: 11 }} sx={{ pb: { xs: 0, md: 0 } }}>
        <SectionHeader eyebrow={t("productsIntro.eyebrow")} title={t("productsIntro.title")} lead={t("productsIntro.lead")} />
      </Section>
      {products.map((project, i) => (
        <ProductFeature key={project.id} project={project} reverse={i % 2 === 1} />
      ))}
    </Box>
  );
}

function Stats() {
  const { t } = useTranslation("HomePage");
  return (
    <Section tone="paper" py={{ xs: 7, md: 9 }}>
      <Grid container spacing={{ xs: 4, md: 4 }}>
        {["s1", "s2", "s3", "s4"].map((k) => (
          <Grid item xs={6} md={3} key={k}>
            <Box sx={{ borderTop: `2px solid ${colors.ink}`, pt: 2.5 }}>
              <Typography sx={{ fontSize: { xs: "2.2rem", md: "2.9rem" }, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1, color: colors.ink }}>
                {t(`stats.${k}.value`)}
              </Typography>
              <Typography sx={{ mt: 1.25, fontSize: "0.95rem", color: colors.body, maxWidth: 230 }}>
                {t(`stats.${k}.label`)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function Work() {
  const { t } = useTranslation("HomePage");
  return (
    <Section tone="warm" py={{ xs: 9, md: 13 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 3 }}>
        <SectionHeader eyebrow={t("work.eyebrow")} title={t("work.title")} lead={t("work.lead")} sx={{ mb: { xs: 4, md: 7 } }} />
        <Box sx={{ mb: { xs: 4, md: 7 } }}>
          <ArrowLink to="/ourwork">{t("work.all")}</ArrowLink>
        </Box>
      </Box>
      <ProjectCard project={projectById(FEATURED.large)} large />
      <Grid container spacing={{ xs: 6, md: 4 }} sx={{ mt: { xs: 1, md: 2 } }}>
        {FEATURED.grid.map((id) => (
          <Grid item xs={12} sm={6} md={4} key={id}>
            <ProjectCard project={projectById(id)} />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

function Services() {
  const { t } = useTranslation("HomePage");
  return (
    <Section tone="paper" py={{ xs: 9, md: 13 }}>
      <SectionHeader eyebrow={t("services.eyebrow")} title={t("services.title")} lead={t("services.lead")} />
      <Grid container spacing={{ xs: 4, md: 4 }}>
        {["s1", "s2", "s3", "s4"].map((k, i) => (
          <Grid item xs={12} sm={6} md={3} key={k}>
            <Box sx={{ borderTop: `1px solid ${colors.ink}`, pt: 2.5 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: colors.orangeDark, mb: 1.5 }}>
                {String(i + 1).padStart(2, "0")}
              </Typography>
              <Typography variant="h4" component="h3" sx={{ mb: 1.25 }}>{t(`services.${k}.title`)}</Typography>
              <Typography sx={{ color: colors.body, fontSize: "0.97rem" }}>{t(`services.${k}.body`)}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 6 }}>
        <ArrowLink to="/services">{t("services.more")}</ArrowLink>
      </Box>
    </Section>
  );
}

function Investors() {
  const { t } = useTranslation("HomePage");
  return (
    <Section tone="warm" py={{ xs: 9, md: 13 }}>
      <Grid container spacing={{ xs: 6, md: 10 }}>
        <Grid item xs={12} md={7}>
          <Eyebrow>{t("investors.eyebrow")}</Eyebrow>
          <Typography variant="h2" component="h2">{t("investors.title")}</Typography>
          <Typography sx={{ mt: 3, color: colors.body, fontSize: "1.05rem" }}>{t("investors.p1")}</Typography>
          <Typography sx={{ mt: 2, color: colors.body, fontSize: "1.05rem" }}>{t("investors.p2")}</Typography>
          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 4 }}>
            {["p1", "p2", "p3"].map((k) => (
              <Box component="li" key={k} sx={{ display: "flex", gap: 1.5, py: 1.25, borderTop: `1px solid ${colors.line}` }}>
                <CheckIcon sx={{ color: colors.orangeDark, fontSize: "1.25rem", mt: 0.3 }} />
                <Typography sx={{ color: colors.ink }}>{t(`investors.points.${k}`)}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ backgroundColor: colors.paper, border: `1px solid ${colors.line}`, borderRadius: "16px", p: { xs: 3.5, md: 5 }, position: { md: "sticky" }, top: { md: 110 } }}>
            <Typography variant="h3" component="h3">{t("investors.cardTitle")}</Typography>
            <Typography sx={{ mt: 2, color: colors.body }}>{t("investors.cardBody")}</Typography>
            <Box sx={{ mt: 3, mb: 3.5, fontSize: "0.97rem", lineHeight: 1.9 }}>
              <Box component="a" href={`mailto:${company.email}`} sx={{ display: "block", color: colors.ink, fontWeight: 600 }}>{company.email}</Box>
              <Box component="a" href={company.phoneHref} sx={{ display: "block", color: colors.ink, fontWeight: 600 }}>{company.phone}</Box>
            </Box>
            <PrimaryButton to="/contact">{t("investors.cardCta")}</PrimaryButton>
          </Box>
        </Grid>
      </Grid>
    </Section>
  );
}

export default function Home() {
  const { t } = useTranslation("HomePage");
  return (
    <>
      <Helmet>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} data-rh="true" />
        <link rel="canonical" href="https://oxide-digital.com/" />
      </Helmet>
      <Hero />
      <Clients />
      <Products />
      <Stats />
      <Work />
      <Services />
      <Investors />
      <CtaBand />
    </>
  );
}
